/**
 * Authentication Context Provider
 * 
 * Manages authentication state throughout the application using AWS Amplify and Cognito.
 * Provides user information and authentication methods to child components.
 * 
 * Features:
 * - OAuth 2.0 authentication flow with Cognito
 * - Session persistence and automatic token refresh
 * - Session expiration handling
 * - Dev bypass when Cognito env vars are not set
 * 
 * Requirements: 1.3, 1.5, 1.6, 5.1, 5.2, 5.3, 15.4
 */

/**
 * Architecture Decision: No Cognito Identity Pool
 *
 * A Cognito Identity Pool is not required for the current application architecture.
 * All frontend data access goes through API Gateway with a CognitoUserPoolsAuthorizer
 * that validates JWT tokens — no frontend code makes direct AWS SDK calls to DynamoDB,
 * SQS, or other AWS services.
 *
 * The only exception is FileService S3 direct uploads, which currently use AWS SDK
 * credentials obtained from the browser. Until this is addressed, FileService retains
 * an optional AWSCredentials parameter solely for S3 upload operations.
 *
 * Recommendation: migrate FileService S3 uploads to presigned URL uploads via API
 * Gateway as a future improvement, eliminating the last dependency on browser-side
 * AWS SDK credentials entirely.
 *
 * Requirements: 8.1, 8.2, 8.3, 8.4
 */

import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import type { AuthUser } from 'aws-amplify/auth';

/**
 * Check if Cognito is configured. When env vars are missing,
 * we enable a local dev bypass so the UI can be explored without auth.
 */
const isPreviewMode = import.meta.env.VITE_PREVIEW_MODE === 'true';

const isCognitoConfigured =
    !isPreviewMode &&
    !!import.meta.env.VITE_COGNITO_USER_POOL_ID &&
    !!import.meta.env.VITE_COGNITO_CLIENT_ID;

/**
 * Lazily load Amplify auth functions only when Cognito is actually configured.
 * This prevents runtime crashes when aws-amplify is imported without configuration.
 */
async function getAmplifyAuth() {
    const mod = await import('aws-amplify/auth');
    return {
        fetchAuthSession: mod.fetchAuthSession,
        signInWithRedirect: mod.signInWithRedirect,
        amplifySignOut: mod.signOut,
        getCurrentUser: mod.getCurrentUser,
    };
}

/**
 * Idle timeout configuration from environment variables
 */
const IDLE_TIMEOUT_MS = Number(import.meta.env.VITE_IDLE_TIMEOUT_MS) || 1_800_000; // 30 min
const WARNING_BEFORE_TIMEOUT_MS = Number(import.meta.env.VITE_WARNING_BEFORE_TIMEOUT_MS) || 120_000; // 2 min

/**
 * Authentication context value interface
 */
export interface AuthContextValue {
    /** Current authenticated user, null if not authenticated */
    user: AuthUser | null;

    /** Whether the user is currently authenticated */
    isAuthenticated: boolean;

    /** Whether authentication state is being checked */
    isLoading: boolean;

    /** Error message if authentication fails */
    error: string | null;

    /** User's email from Cognito ID token */
    email: string | null;

    /** Initiates sign-in flow via Cognito OAuth */
    signIn: () => Promise<void>;

    /** Signs out the current user and clears session */
    signOut: () => Promise<void>;

    /** Refreshes the current authentication session */
    refreshSession: () => Promise<void>;

    /** Whether the idle warning dialog should be shown */
    showIdleWarning: boolean;

    /** Seconds remaining before auto-signout */
    idleCountdown: number;

    /** Reset idle timer and refresh session (called from warning dialog) */
    staySignedIn: () => void;
}

/**
 * Authentication context
 */
export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Props for AuthProvider component
 */
interface AuthProviderProps {
    children: React.ReactNode;
}

/**
 * Authentication Provider Component
 * 
 * Wraps the application to provide authentication state and methods.
 * Automatically checks authentication state on mount and handles OAuth callbacks.
 * 
 * @example
 * ```tsx
 * <AuthProvider>
 *   <App />
 * </AuthProvider>
 * ```
 */
export function AuthProvider({ children }: AuthProviderProps) {
    // In dev mode (no Cognito), start already authenticated so ProtectedRoute
    // never redirects to /login before the first render.
    const devUser = !isCognitoConfigured && !isPreviewMode
        ? ({ username: 'dev-user', userId: 'dev-local-001' } as AuthUser)
        : null;

    const [user, setUser] = useState<AuthUser | null>(devUser);
    const [email, setEmail] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(isCognitoConfigured); // false in dev/preview mode
    const [error, setError] = useState<string | null>(null);

    /**
     * Checks the current authentication state
     * Fetches user information if authenticated
     */
    const checkAuthState = useCallback(async (options?: { silent?: boolean }) => {
        const silent = options?.silent ?? false;

        try {
            if (!silent) {
                setIsLoading(true);
            }
            setError(null);

            const { fetchAuthSession, getCurrentUser } = await getAmplifyAuth();

            // Fetch the current auth session
            const session = await fetchAuthSession();

            // Check if we have valid tokens
            if (session.tokens) {
                // Get current user information
                const currentUser = await getCurrentUser();
                setUser(currentUser);

                // Extract email from ID token claims
                const idToken = session.tokens?.idToken;
                const tokenEmail = idToken?.payload?.email as string | undefined;
                setEmail(tokenEmail || null);

                // Fire-and-forget pre-warm on session init (Req 6.6)
                // Dedup is handled server-side by the Instance Registry's
                // acquire_pre_warm_lock() — no client-side sessionStorage guard needed.
                // React Strict Mode may double-fire this; the second request will be
                // rejected atomically by the DynamoDB conditional write (no harm done).
                try {
                    const { UserProfileService } = await import('../services/user-profile-service');
                    const { AuthenticatedApiClient } = await import('../services/authenticated-api-client');
                    const profileService = new UserProfileService();
                    const profile = await profileService.getProfile();
                    const PRE_WARM_TIERS = ['Gold', 'Silver', 'Bronze', 'Iron', 'Free'];
                    if (PRE_WARM_TIERS.includes(profile.tier)) {
                        const client = new AuthenticatedApiClient();
                        const payload = JSON.stringify({ source: 'pre-warm', user_id: profile.user_id, user_tier: profile.tier });
                        const MAX_RETRIES = 3;
                        const firePreWarm = async () => {
                            for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
                                try {
                                    await client.apiRequest('/pre-warm', {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: payload,
                                    });
                                    return; // Success — stop retrying
                                } catch {
                                    if (attempt < MAX_RETRIES - 1) {
                                        // Exponential backoff: 2s, 4s
                                        await new Promise(r => setTimeout(r, 2000 * (attempt + 1)));
                                    }
                                }
                            }
                        };
                        firePreWarm().catch(() => { }); // Non-blocking
                    }
                } catch {
                    // Pre-warm is best-effort — don't affect auth flow
                }
            } else {
                setUser(null);
                setEmail(null);
            }
        } catch (err) {
            console.error('Error checking auth state:', err);
            setUser(null);
            setEmail(null);

            if (err instanceof Error && !err.message.includes('not authenticated')) {
                setError(err.message);
            }
        } finally {
            if (!silent) {
                setIsLoading(false);
            }
        }
    }, []);

    /**
     * Check authentication state on mount.
     * In dev bypass mode, log a single warning (Requirement 10.4).
     */
    useEffect(() => {
        if (isCognitoConfigured) {
            checkAuthState();
        } else {
            console.warn('[Dev Mode] Cognito not configured — using dev bypass authentication');
        }
    }, [checkAuthState]);

    /**
     * Visibility-change session refresh (Requirement 9.1, 9.4, 9.5)
     *
     * When the tab becomes visible and the user is authenticated, silently
     * refresh the session with forceRefresh: true. On failure, clear auth state
     * so ProtectedRoute redirects to LoginPage.
     */
    const userRef = useRef(user);
    userRef.current = user;

    useEffect(() => {
        const handleVisibilityChange = async () => {
            if (document.visibilityState !== 'visible') return;
            if (!userRef.current) return;
            if (!isCognitoConfigured) return;

            try {
                const { fetchAuthSession, getCurrentUser } = await getAmplifyAuth();
                const session = await fetchAuthSession({ forceRefresh: true });

                if (session.tokens) {
                    const currentUser = await getCurrentUser();
                    setUser(currentUser);
                    const tokenEmail = session.tokens?.idToken?.payload?.email as string | undefined;
                    setEmail(tokenEmail || null);
                } else {
                    // Session expired — no tokens after refresh
                    console.warn('[AuthContext] Session expired on visibility change — clearing auth state');
                    setUser(null);
                    setEmail(null);
                }
            } catch (err) {
                // Log without exposing token data (Requirement 9.5)
                console.error('[AuthContext] Session refresh failed on visibility change');
                setUser(null);
                setEmail(null);
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    // ── Idle session timeout (Requirements 12.1–12.9) ──────────────────

    const [showIdleWarning, setShowIdleWarning] = useState(false);
    const [idleCountdown, setIdleCountdown] = useState(0);

    // Mutable refs so interval/event callbacks always see latest values
    const lastActivityRef = useRef(Date.now());
    const tabHiddenAtRef = useRef<number | null>(null);
    const elapsedBeforeHideRef = useRef(0);
    const isAuthenticatedRef = useRef(!!user);
    isAuthenticatedRef.current = !!user;

    /** Reset idle timer to now */
    const resetIdleTimer = useCallback(() => {
        lastActivityRef.current = Date.now();
        elapsedBeforeHideRef.current = 0;
        tabHiddenAtRef.current = null;
        setShowIdleWarning(false);
        setIdleCountdown(0);
    }, []);

    /** "Stay Signed In" handler — reset timer + silent session refresh */
    const staySignedIn = useCallback(() => {
        resetIdleTimer();
        // Fire-and-forget silent refresh
        if (isCognitoConfigured) {
            getAmplifyAuth()
                .then(({ fetchAuthSession }) => fetchAuthSession({ forceRefresh: true }))
                .catch(() => {
                    console.error('[AuthContext] Silent refresh on staySignedIn failed');
                });
        }
    }, [resetIdleTimer]);

    /**
     * Activity event listeners — throttled to max 1/sec (Requirement 12.9)
     * Disabled in dev bypass mode (Requirement 12.7)
     */
    useEffect(() => {
        if (!isCognitoConfigured) return; // dev bypass — no idle timeout

        let lastThrottleTime = 0;
        const THROTTLE_MS = 1000;

        const handleActivity = () => {
            const now = Date.now();
            if (now - lastThrottleTime < THROTTLE_MS) return;
            lastThrottleTime = now;

            if (!isAuthenticatedRef.current) return;
            lastActivityRef.current = now;
            elapsedBeforeHideRef.current = 0;
            tabHiddenAtRef.current = null;
            setShowIdleWarning(false);
        };

        const events = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll'] as const;
        events.forEach((evt) => window.addEventListener(evt, handleActivity, { passive: true }));

        return () => {
            events.forEach((evt) => window.removeEventListener(evt, handleActivity));
        };
    }, []);

    /**
     * Idle timer tick — runs every second, checks elapsed idle time.
     * Pauses when tab is hidden, resumes when visible (Requirement 12.8).
     */
    useEffect(() => {
        if (!isCognitoConfigured) return; // dev bypass

        const handleIdleVisibility = () => {
            if (document.visibilityState === 'hidden') {
                // Pause: record how much idle time has elapsed so far
                const elapsed = Date.now() - lastActivityRef.current;
                elapsedBeforeHideRef.current = elapsed;
                tabHiddenAtRef.current = Date.now();
            } else {
                // Resume: adjust lastActivity so elapsed time is preserved
                // (includes time spent hidden)
                if (tabHiddenAtRef.current !== null) {
                    const hiddenDuration = Date.now() - tabHiddenAtRef.current;
                    const totalElapsed = elapsedBeforeHideRef.current + hiddenDuration;
                    lastActivityRef.current = Date.now() - totalElapsed;
                    tabHiddenAtRef.current = null;

                    // If total idle time (visible + hidden) exceeds timeout, sign out immediately
                    if (totalElapsed >= IDLE_TIMEOUT_MS && isAuthenticatedRef.current) {
                        handleSignOutForIdle();
                        return;
                    }
                }
            }
        };

        document.addEventListener('visibilitychange', handleIdleVisibility);

        const intervalId = setInterval(() => {
            if (!isAuthenticatedRef.current) return;
            // Don't advance timer while tab is hidden
            if (document.visibilityState === 'hidden') return;

            const elapsed = Date.now() - lastActivityRef.current;
            const warningThreshold = IDLE_TIMEOUT_MS - WARNING_BEFORE_TIMEOUT_MS;

            if (elapsed >= IDLE_TIMEOUT_MS) {
                // Timeout expired — sign out (Requirement 12.6)
                clearInterval(intervalId);
                setShowIdleWarning(false);
                setIdleCountdown(0);
                // Sign out and redirect
                handleSignOutForIdle();
            } else if (elapsed >= warningThreshold) {
                // In warning zone — show dialog with countdown (Requirement 12.3)
                const remainingMs = IDLE_TIMEOUT_MS - elapsed;
                const remainingSec = Math.ceil(remainingMs / 1000);
                setShowIdleWarning(true);
                setIdleCountdown(remainingSec);
            } else {
                setShowIdleWarning(false);
                setIdleCountdown(0);
            }
        }, 1000);

        return () => {
            clearInterval(intervalId);
            document.removeEventListener('visibilitychange', handleIdleVisibility);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Sign out due to idle timeout — clears state and redirects to /login?reason=idle
     */
    const handleSignOutForIdle = useCallback(async () => {
        try {
            if (isCognitoConfigured) {
                const { amplifySignOut } = await getAmplifyAuth();
                await amplifySignOut();
            }
        } catch (err) {
            console.error('[AuthContext] Error during idle sign-out');
        } finally {
            setUser(null);
            setEmail(null);
            setShowIdleWarning(false);
            setIdleCountdown(0);
            localStorage.setItem('auth-sign-out', 'true');
            localStorage.removeItem('auth-sign-out');
            window.location.href = '/login?reason=idle';
        }
    }, []);

    /**
     * Initiates the sign-in flow using Cognito OAuth
     * Redirects to Cognito hosted UI
     */
    const handleSignIn = useCallback(async () => {
        if (isPreviewMode) return;
        try {
            setError(null);
            if (!isCognitoConfigured) {
                setUser({ username: 'dev-user', userId: 'dev-local-001' } as AuthUser);
                return;
            }
            const { signInWithRedirect } = await getAmplifyAuth();
            await signInWithRedirect();
        } catch (err) {
            console.error('Error signing in:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to sign in';
            setError(errorMessage);
            throw err;
        }
    }, []);

    /**
     * Signs out the current user
     * Clears local session and redirects to sign-out URL
     */
    const handleSignOut = useCallback(async () => {
        try {
            setError(null);
            if (!isCognitoConfigured) {
                setUser(null);
                setEmail(null);
                return;
            }
            const { amplifySignOut } = await getAmplifyAuth();
            await amplifySignOut();
            setUser(null);
            setEmail(null);
            localStorage.setItem('auth-sign-out', 'true');
            localStorage.removeItem('auth-sign-out');
        } catch (err) {
            console.error('Error signing out:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to sign out';
            setError(errorMessage);
            throw err;
        }
    }, []);

    /**
     * Refreshes the current authentication session
     */
    const refreshSession = useCallback(async () => {
        await checkAuthState();
    }, [checkAuthState]);

    // ── Cross-tab session sync (broadcast sign-out to other tabs) ────────
    useEffect(() => {
        if (!isCognitoConfigured) return;

        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === 'auth-sign-out' && e.newValue === 'true') {
                setUser(null);
                setEmail(null);
                setShowIdleWarning(false);
                setIdleCountdown(0);
            }
        };
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    const value: AuthContextValue = {
        user,
        isAuthenticated: !!user,
        isLoading,
        error,
        email,
        signIn: handleSignIn,
        signOut: handleSignOut,
        refreshSession,
        showIdleWarning,
        idleCountdown,
        staySignedIn,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access authentication context
 * 
 * @throws Error if used outside of AuthProvider
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { user, isAuthenticated, signIn, signOut } = useAuth();
 *   
 *   if (!isAuthenticated) {
 *     return <button onClick={signIn}>Sign In</button>;
 *   }
 *   
 *   return (
 *     <div>
 *       <p>Welcome, {user?.username}</p>
 *       <button onClick={signOut}>Sign Out</button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useAuth(): AuthContextValue {
    const context = useContext(AuthContext);

    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }

    return context;
}
